import { useEffect, useRef, useState } from "react"

import cuid from "cuid"
import { format } from "date-fns"
import { Form, Link, useFetcher, useLoaderData, useSearchParams, useSubmit } from "react-router-dom"
import { twMerge } from "tailwind-merge"

import { Bee } from "~/src/components/BeeKit"
import { basename } from "~/src/components/Controller/DashboardLookbooksRoutes"
import { EmptyResults } from "~/src/components/EmptyResults"
import { ImageSet } from "~/src/components/ImageSet"
import { MainHeading } from "~/src/components/MainHeading"
import { PagyNav } from "~/src/components/PagyNav"
import { Pagy } from "~/src/serializedRecords"
import { Photo } from "~/src/types/photo"

//region Types
export type Lookbook = {
  id: string
  name: string
  sentAt?: string
  createdAt: string
  unseen?: boolean
}

export type DashboardLookbooksIndexProps = {
  meta: Pagy
  items: Lookbook[]
}
//endregion

//region Component
export function DashboardLookbooksIndex() {
  const {
    items,
    meta: { previous, next, series, pages },
  } = useLoaderData() as DashboardLookbooksIndexProps

  const [searchParams] = useSearchParams()
  const [searchValue, setSearchValue] = useState<string>(searchParams.get("search") ?? "")
  const formRef = useRef<HTMLFormElement>(null)
  const submit = useSubmit()

  useEffect(() => {
    if (searchValue != searchParams.get("search")) {
      setSearchValue(searchParams.get("search") ?? "")
    }
  }, [searchParams])

  return (
    <>
      <MainHeading title="Your Lookbooks" />
      <hr />

      <div className={twMerge("mb-10 flex w-full flex-col gap-6 p-8 sm:mb-0 md:w-5/6 lg:max-w-max")}>
        <Form ref={formRef} method="get" className="contents">
          <Bee.Searchbar
            name="search"
            value={searchValue}
            onChange={(event) => setSearchValue(event.currentTarget.value)}
            onClear={() => {
              setSearchValue("")

              if (!formRef.current) return

              const formData = new FormData(formRef.current)
              formData.delete("search")

              submit(formData)
            }}
          />
        </Form>

        {items.length === 0 ? (
          <EmptyResults className="shrink-1 w-full" />
        ) : (
          <ul className="flex w-full flex-col items-start gap-6">
            {items.map(({ id, name, sentAt, createdAt, unseen = false }) => {
              return (
                <LookbookItem
                  key={id}
                  className="w-full"
                  id={id}
                  name={name}
                  at={sentAt ?? createdAt}
                  unseen={unseen}
                />
              )
            })}

            {series.length > 0 && pages > 1 && (
              <div className="flex w-full justify-center">
                <PagyNav previous={previous} next={next} series={series} />
              </div>
            )}
          </ul>
        )}
        <BlankLookbookItem />
      </div>
    </>
  )
}
//endregion

//region Private
type LookbookItemProps = {
  id: string
  className?: string
  at: string
  name: string
  unseen: boolean
}

type LatestProduct = {
  id: string
  placeholder?: boolean
  photo: Photo
}

const placeholderProducts = Array.from({ length: 5 }, () => ({ id: cuid(), placeholder: true }))

/**
 * Component representing an individual lookbook item.
 *
 * @param props
 * @param props.className Additional class names to apply to the component.
 * @param props.at The date associated with the lookbook item.
 * @param props.name The name of the lookbook item.
 * @param props.products An array of products included in the lookbook item.
 *
 * @returns A JSX element representing a lookbook item.
 */
function LookbookItem(props: LookbookItemProps) {
  const { id, className, at, name, unseen = false } = props

  const fetcher = useFetcher({ key: `${id}/latest_products` })

  useEffect(() => {
    if (fetcher.state === "idle" && !fetcher.data) {
      fetcher.load(`${id}/latest_products`)
    }
  }, [fetcher.state, fetcher.data, id])

  const products: LatestProduct[] = fetcher.data?.products ?? placeholderProducts

  return (
    <li className={twMerge("flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-6", className)}>
      <Link to={`${basename}/${id}`} className="contents">
        <hgroup className="flex max-w-max cursor-pointer flex-col">
          <div className="flex place-items-center gap-3">
            <h1 className="text-lg font-semibold">{name}</h1>
            {unseen ? <p className="rounded-full bg-magenta-500 px-2 py-1 text-xs font-bold text-white">New</p> : null}
          </div>
          {at && <p className="text-sm text-gray-500">{format(at, "MMMM yyyy")}</p>}
        </hgroup>
      </Link>

      <ul className="grid grid-cols-2 gap-4 sm:flex sm:grid-cols-none sm:flex-wrap sm:gap-4">
        {products.map(({ id: productId, photo, placeholder }, index) => {
          return (
            <li
              key={productId}
              className={twMerge(
                "aspect-square overflow-hidden rounded-lg border border-gray-200",
                index >= 4 && "hidden sm:block"
              )}
            >
              <Link to={`${basename}/${id}`} className="contents">
                {placeholder ? (
                  <div className="aspect-square h-auto w-full rounded-lg bg-white sm:w-40" />
                ) : (
                  <ImageSet className="w-full sm:h-40 sm:w-40" srcs={[photo.largeUrl, photo.xlargeUrl]} />
                )}
              </Link>
            </li>
          )
        })}
      </ul>
    </li>
  )
}

/**
 * Exists for the sole purpose of having a baseline width equal to a <LookbookItem /> with
 * 5 products.
 * @returns
 */
function BlankLookbookItem({ className }: { className?: string }) {
  return (
    <li
      className={twMerge(
        "flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-6",
        "invisible h-0 overflow-hidden px-6 py-0",
        className
      )}
    >
      <ul className="grid grid-cols-2 gap-4 sm:flex sm:grid-cols-none sm:flex-wrap sm:gap-4">
        {placeholderProducts.map(({ id }, index) => (
          <li
            key={id}
            className={twMerge(
              "aspect-square overflow-hidden rounded-lg border border-gray-200",
              index >= 4 && "hidden sm:block"
            )}
          >
            <div className="aspect-square h-auto w-full rounded-lg bg-white sm:w-40" />
          </li>
        ))}
      </ul>
    </li>
  )
}
//endregion
